<script setup lang="ts">
import { Check, Clipboard, Eye, EyeOff, KeyRound, LoaderCircle, Plus, ShieldCheck, Trash2, X } from "lucide-vue-next"
import { computed, onMounted, ref } from "vue"

import AppActionDialog from "../components/AppActionDialog.vue"
import { ApiError } from "../lib/api"
import { formatShanghaiTime } from "../lib/datetime"
import { useApiKeysStore } from "../stores/apiKeys"

const apiKeys = useApiKeysStore()
const creating = ref(false)
const name = ref("Playground")
const submitting = ref(false)
const revealed = ref(true)
const copied = ref(false)
const errorMessage = ref("")
const revokeTarget = ref<{ id: string; name: string } | null>(null)

const displayKey = computed(() => {
  const plaintext = apiKeys.revealedKey?.plaintext
  if (!plaintext) return ""
  return revealed.value ? plaintext : `${plaintext.slice(0, 20)}${"•".repeat(20)}`
})

onMounted(() => apiKeys.load())

async function createKey(): Promise<void> {
  errorMessage.value = ""
  submitting.value = true
  try {
    await apiKeys.create(name.value)
    creating.value = false
    revealed.value = true
  } catch (error) {
    errorMessage.value = error instanceof ApiError ? error.message : "创建失败，请稍后重试。"
  } finally {
    submitting.value = false
  }
}

async function copyKey(): Promise<void> {
  if (!apiKeys.revealedKey) return
  await navigator.clipboard.writeText(apiKeys.revealedKey.plaintext)
  copied.value = true
  window.setTimeout(() => (copied.value = false), 1600)
}

async function revokeKey(): Promise<void> {
  if (!revokeTarget.value) return
  submitting.value = true
  try {
    await apiKeys.revoke(revokeTarget.value.id)
    revokeTarget.value = null
  } catch (cause) {
    errorMessage.value = cause instanceof Error ? cause.message : "吊销失败"
  } finally {
    submitting.value = false
  }
}

function formatDate(value: string): string {
  return formatShanghaiTime(value, { dateStyle: "medium" })
}
</script>

<template>
  <section data-screen-label="API Keys">
    <div class="section-heading section-heading--page">
      <div class="page-heading"><span class="eyebrow">Credentials</span><h1>API Keys</h1><p>为不同用途和集成创建独立 Key。完整值只在创建时展示一次。</p></div>
      <button class="primary-button" @click="creating = true"><Plus :size="16" />创建 API Key</button>
    </div>

    <div v-if="apiKeys.revealedKey" class="secret-reveal" role="status">
      <div class="secret-reveal__heading"><span><ShieldCheck :size="18" />保存这把 Key</span><button class="icon-button" aria-label="关闭" @click="apiKeys.revealedKey = null"><X :size="17" /></button></div>
      <p>关闭后无法再次查看完整值。不要把它提交到代码仓库或写入前端持久化存储。</p>
      <div class="secret-value"><code>{{ displayKey }}</code><button class="icon-button" :aria-label="revealed ? '隐藏' : '显示'" @click="revealed = !revealed"><EyeOff v-if="revealed" :size="17" /><Eye v-else :size="17" /></button><button class="copy-button" @click="copyKey"><Check v-if="copied" :size="15" /><Clipboard v-else :size="15" />{{ copied ? "已复制" : "复制" }}</button></div>
      <RouterLink to="/playground" class="inline-link">带到 Playground 使用 →</RouterLink>
    </div>

    <div class="table-shell">
      <div class="table-toolbar"><span>{{ apiKeys.items.length }} 把 Key</span><span class="eyebrow">ACCESS KEYS</span></div>
      <div v-if="apiKeys.loading" class="empty-state"><LoaderCircle class="spin" :size="20" /><span>正在加载 API Keys</span></div>
      <div v-else-if="apiKeys.items.length === 0" class="empty-state"><KeyRound :size="24" /><strong>还没有 API Key</strong><p>创建第一把 API Key，用于 Playground 和本地开发。</p></div>
      <div v-else class="responsive-table">
        <table>
          <thead><tr><th>名称</th><th>密钥</th><th>状态</th><th>创建时间</th><th><span class="sr-only">操作</span></th></tr></thead>
          <tbody>
            <tr v-for="key in apiKeys.items" :key="key.id">
              <td><strong>{{ key.name }}</strong></td>
              <td><code>••••••••••••{{ key.last_four }}</code></td>
              <td><span class="status-pill" :class="`status-pill--${key.status}`"><i></i>{{ key.status }}</span></td>
              <td>{{ formatDate(key.created_at) }}</td>
              <td><button v-if="key.status === 'active'" class="danger-icon" aria-label="吊销 Key" @click="revokeTarget = { id: key.id, name: key.name }"><Trash2 :size="16" /></button></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="creating" class="modal-backdrop" @click.self="creating = false">
      <form class="modal" @submit.prevent="createKey">
        <div class="modal__heading"><div><span class="eyebrow">New credential</span><h2>创建 API Key</h2></div><button type="button" class="icon-button" @click="creating = false"><X :size="18" /></button></div>
        <label class="field"><span>名称</span><input v-model="name" required maxlength="120" autofocus placeholder="例如：Local development" /></label>
        <div class="permission-summary"><div><span>权限</span><strong>只读</strong></div><div><span>Scope</span><strong>content:read</strong></div><div><span>平台</span><strong>六平台</strong></div></div>
        <p v-if="errorMessage" class="form-error">{{ errorMessage }}</p>
        <div class="modal__actions"><button type="button" class="secondary-button" @click="creating = false">取消</button><button class="primary-button" :disabled="submitting"><LoaderCircle v-if="submitting" class="spin" :size="16" />创建 Key</button></div>
      </form>
    </div>

    <AppActionDialog
      :open="Boolean(revokeTarget)"
      eyebrow="Revoke credential"
      title="确认吊销 API Key"
      description="吊销后无法恢复，使用这把 Key 的接口调用会立即失败。"
      :detail="revokeTarget?.name ?? ''"
      confirm-label="确认吊销"
      danger
      :busy="submitting"
      @close="revokeTarget = null"
      @confirm="revokeKey"
    />
  </section>
</template>
